// src/components/DetalleParqueIndustrial.js
//
// Modal de detalle para Parques Industriales — mismo esqueleto/UX que
// DetalleIntervencionMercado pero con los datos y secciones propios de un
// parque industrial. Se monta dentro del .popup-shell que provee RightPanel.

import React, { useEffect, useState } from "react";
import parquesIndustriales from "../services/parquesService";
import { AppConfig } from "../config/appConfig";
import CloseIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibraryOutlined";
import FullscreenIcon from "@mui/icons-material/FullscreenRounded";
import ImageModal from "./ImageModal";
import "./DetalleParqueIndustrial.css";
import "./popup-styles.css"; // reutiliza .parq-popup-section/.parq-popup-kv

// Fallback hardcodeado de imágenes para parques cuya data en backend
// (listarImagenesParque) viene vacía. Mismas URLs que usaba la app legacy
// (mapa/) — algunos parques no tienen sus fotos cargadas en la base pero
// existen en dimici.produce.gob.pe/zonas_img/. Regla: si la API devuelve
// imágenes las usamos; si no, caemos a este mapa.
// Indexamos por id_intervencion Y por nombre (uppercase) porque las IDs
// pueden variar entre ambientes; el nombre es más estable.
const _m = () => AppConfig.MEDIA_BASE_URL;
const FALLBACK_IMAGES_BY_INTERVENCION = {
  // id_intervencion → URLs (construidas en runtime desde MEDIA_BASE_URL)
  403: () => [`${_m()}/zonas_img/PI ANCON.png`],
  404: () => [
    `${_m()}/zonas_img/indupark1.png`,
    `${_m()}/zonas_img/indupark2.png`,
    `${_m()}/zonas_img/indupark3.png`,
    `${_m()}/zonas_img/indupark4.png`,
    `${_m()}/zonas_img/indupark5.png`,
  ],
  405: () => [
    `${_m()}/zonas_img/sector62_1.png`,
    `${_m()}/zonas_img/sector62_2.png`,
    `${_m()}/zonas_img/sector62_3.png`,
    `${_m()}/zonas_img/sector62_4.png`,
  ],
  406: () => [
    `${_m()}/zonas_img/chutana1.png`,
    `${_m()}/zonas_img/chutana2.png`,
    `${_m()}/zonas_img/chutana3.png`,
    `${_m()}/zonas_img/chutana4.png`,
    `${_m()}/zonas_img/chutana5.png`,
  ],
  830: () => [
    `${_m()}/zonas_img/piura_futura_1.jpg`,
    `${_m()}/zonas_img/piura_futura_2.png`,
    `${_m()}/zonas_img/piura_futura_3.png`,
  ],
};

// Lookup secundario por NOMBRE — más estable entre ambientes si las IDs
// cambian. La key se compara contra el nombre del parque normalizado
// (trim + uppercase).
const FALLBACK_IMAGES_BY_NAME = {
  "INDUPARK": FALLBACK_IMAGES_BY_INTERVENCION[404],
  "PI INDUPARK": FALLBACK_IMAGES_BY_INTERVENCION[404],
  "ANCON": FALLBACK_IMAGES_BY_INTERVENCION[403],
  "PI ANCON": FALLBACK_IMAGES_BY_INTERVENCION[403],
  "PI ANCÓN": FALLBACK_IMAGES_BY_INTERVENCION[403],
  "SECTOR 62": FALLBACK_IMAGES_BY_INTERVENCION[405],
  "PI SECTOR 62": FALLBACK_IMAGES_BY_INTERVENCION[405],
  "LA CHUTANA": FALLBACK_IMAGES_BY_INTERVENCION[406],
  "PI LA CHUTANA": FALLBACK_IMAGES_BY_INTERVENCION[406],
  "CHUTANA": FALLBACK_IMAGES_BY_INTERVENCION[406],
  "PIURA FUTURA": FALLBACK_IMAGES_BY_INTERVENCION[830],
};

// Acepta varios candidatos de nombre — la API devuelve `nombre_proyecto`
// que puede diferir del `valor_descripcion` del feature ArcGIS. Probamos
// todos para maximizar la chance de match.
const _resolveFallbackImages = (idIntervencion, ...nameCandidates) => {
  // Resuelve la función o array del mapa (soporta ambas formas)
  const _resolve = (val) => (typeof val === "function" ? val() : val);
  // 1) Por id (acepta tanto número como string)
  const byId =
    _resolve(FALLBACK_IMAGES_BY_INTERVENCION[idIntervencion]) ||
    _resolve(FALLBACK_IMAGES_BY_INTERVENCION[String(idIntervencion)]) ||
    _resolve(FALLBACK_IMAGES_BY_INTERVENCION[Number(idIntervencion)]);
  if (byId && byId.length > 0) return byId;
  // 2) Por nombre — para CADA candidato probamos exact match y luego
  // inclusión sobre las keys conocidas. Case-insensitive.
  for (const candidate of nameCandidates) {
    if (!candidate) continue;
    const key = String(candidate).trim().toUpperCase();
    if (!key) continue;
    if (FALLBACK_IMAGES_BY_NAME[key]) return _resolve(FALLBACK_IMAGES_BY_NAME[key]);
    for (const knownName of Object.keys(FALLBACK_IMAGES_BY_NAME)) {
      if (key.includes(knownName)) return _resolve(FALLBACK_IMAGES_BY_NAME[knownName]);
    }
  }
  return [];
};

// id_infraestructura → campo del DTO de IntervencionParamDetalle.
const FIELD_IDS = {
  hectareasTotales: 32,
  hectareasAHabilitar: 33,
  empresasInstaladas: 34,
  principalesEmpresas: 35,
  actividadesPermitidas: 36,
  productosExporta: 37,
  departamento: 38,
  provincia: 39,
  distrito: 40,
  direccion: 41,
  inversion: 43,
  puestosTrabajo: 44,
  exportaciones: 45,
  empresasAInstalar: 46,
  totalEtapas: 47,
  etapasHabilitadas: 48,
  lotesHabilitados: 51,
  lotesDisponibles: 53,
  hectareasHabilitadas: 54,
};

const Gallery = ({ images, loading, altPrefix = "Imagen" }) => {
  const [index, setIndex] = useState(0);
  // Cuando hay una URL aquí, mostramos ImageModal en fullscreen sobre todo.
  const [fullscreenSrc, setFullscreenSrc] = useState(null);

  useEffect(() => {
    setIndex(0);
  }, [images]);

  if (loading) {
    return (
      <div className="pdet-gallery pdet-gallery-empty">
        <div className="pdet-gallery-spinner" />
        <span>Cargando imágenes…</span>
      </div>
    );
  }
  if (!images || images.length === 0) {
    return (
      <div className="pdet-gallery pdet-gallery-empty">
        <PhotoLibraryIcon style={{ fontSize: 32, opacity: 0.55 }} />
        <span>Sin imágenes disponibles</span>
      </div>
    );
  }

  const prev = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };
  const next = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };
  const openFullscreen = (e) => {
    e?.stopPropagation?.();
    setFullscreenSrc(images[index]);
  };

  return (
    <>
      <div className="pdet-gallery">
        <img
          className="pdet-gallery-img pdet-gallery-img--clickable"
          src={images[index]}
          alt={`${altPrefix} ${index + 1}`}
          onClick={openFullscreen}
          title="Ver en pantalla completa"
        />
        {/* Botón fullscreen — siempre visible en la esquina superior derecha
            para que sea descubrible (no solo al pasar el mouse). */}
        <button
          type="button"
          className="pdet-gallery-fullscreen"
          onClick={openFullscreen}
          aria-label="Ver en pantalla completa"
          title="Ver en pantalla completa"
        >
          <FullscreenIcon style={{ fontSize: 20 }} />
        </button>
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="pdet-gallery-nav left"
              onClick={prev}
              aria-label="Imagen anterior"
            >
              <ChevronLeftIcon style={{ fontSize: 28 }} />
            </button>
            <button
              type="button"
              className="pdet-gallery-nav right"
              onClick={next}
              aria-label="Siguiente imagen"
            >
              <ChevronRightIcon style={{ fontSize: 28 }} />
            </button>
            <div className="pdet-gallery-counter">
              {index + 1} / {images.length}
            </div>
          </>
        )}
      </div>
      <ImageModal
        open={!!fullscreenSrc}
        onClose={() => setFullscreenSrc(null)}
        imageUrl={fullscreenSrc || ""}
        altText={`${altPrefix} ${index + 1} de ${images.length}`}
      />
    </>
  );
};

const KV = ({ label, value }) => {
  if (!value || value === "—" || value === "-" || String(value).trim() === "")
    return null;
  return (
    <div className="parq-popup-kv">
      <span className="parq-popup-kv-label">{label}</span>
      <span className="parq-popup-kv-value">{value}</span>
    </div>
  );
};

const Section = ({ title, children }) => {
  // Si todos los children son null (no hay datos) ocultamos la sección.
  const hasContent = React.Children.toArray(children).some((c) => c);
  if (!hasContent) return null;
  return (
    <div className="parq-popup-section">
      <h5 className="parq-popup-section-title">{title}</h5>
      <div className="parq-popup-grid">{children}</div>
    </div>
  );
};

const DetalleParqueIndustrial = ({ idIntervencion, nombre, onClose }) => {
  const [data, setData] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!idIntervencion) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    setImages([]);

    Promise.all([
      parquesIndustriales
        .getIntervencionParamDetalle(idIntervencion, 0)
        .catch(() => null),
      parquesIndustriales
        .listarImagenesParque(idIntervencion)
        .catch(() => ({ items: [] })),
    ]).then(([detailResp, imgResp]) => {
      if (cancelled) return;
      const item = detailResp?.item;
      const params = item?.lista_param || [];
      const getValue = (idInfra) => {
        const row = params.find((x) => x.id_infraestructura === idInfra);
        return (row?.valor_descripcion || "").trim();
      };
      const fields = Object.fromEntries(
        Object.entries(FIELD_IDS).map(([key, id]) => [key, getValue(id)])
      );
      setData({
        ...fields,
        nombre_proyecto: item?.nombre_proyecto || nombre || "Parque industrial",
      });
      // Soporta varias formas de respuesta del backend: { items: [...] },
      // array directo, o objetos con `url`/`urlArchivo`. Filtra entradas
      // vacías.
      const rawList = Array.isArray(imgResp)
        ? imgResp
        : Array.isArray(imgResp?.items)
        ? imgResp.items
        : Array.isArray(imgResp?.data)
        ? imgResp.data
        : [];
      const apiImages = rawList
        .map((img) =>
          typeof img === "string" ? img : img?.url || img?.urlArchivo || ""
        )
        .map((s) => (s || "").trim())
        .filter(Boolean);
      // Preferimos las imágenes que devuelve la API (mismo comportamiento
      // que la app legacy). Solo si la API no trae nada caemos al fallback
      // hardcodeado para los parques conocidos (INDUPARK, ANCÓN, SECTOR 62,
      // CHUTANA, PIURA FUTURA) — esto cubre el caso de ampliaciones u otros
      // parques cuyo backend aún no tiene fotos cargadas.
      const fallback = _resolveFallbackImages(
        idIntervencion,
        item?.nombre_proyecto,
        nombre
      );
      setImages(apiImages.length > 0 ? apiImages : fallback);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [idIntervencion, nombre]);

  return (
    <div className="pdet-card">
      <button
        type="button"
        className="pdet-close"
        onClick={onClose}
        aria-label="Cerrar"
      >
        <CloseIcon style={{ fontSize: 20 }} />
      </button>

      <div className="pdet-card-head">
        <span className="pdet-pill">Parque Industrial</span>
        <h2 className="pdet-title">
          {data?.nombre_proyecto || nombre || "Parque industrial"}
        </h2>
      </div>

      <div className="pdet-card-body">
        <Gallery
          images={images}
          loading={loading}
          altPrefix={`Parque ${data?.nombre_proyecto || nombre || "industrial"} —`}
        />

        {loading ? (
          <div className="pdet-loading">Cargando información…</div>
        ) : (
          data && (
            <>
              <Section title="Ubicación">
                <KV label="Departamento" value={data.departamento} />
                <KV label="Provincia" value={data.provincia} />
                <KV label="Distrito" value={data.distrito} />
                <KV label="Dirección" value={data.direccion} />
              </Section>

              <Section title="Hectáreas">
                <KV label="Totales (ha)" value={data.hectareasTotales} />
                <KV
                  label="Habilitadas (ha)"
                  value={data.hectareasHabilitadas}
                />
                <KV
                  label="A habilitar (ha)"
                  value={data.hectareasAHabilitar}
                />
              </Section>

              <Section title="Etapas y lotes">
                <KV label="Total etapas" value={data.totalEtapas} />
                <KV
                  label="Etapas habilitadas"
                  value={data.etapasHabilitadas}
                />
                <KV
                  label="Lotes habilitados"
                  value={data.lotesHabilitados}
                />
                <KV
                  label="Lotes disponibles"
                  value={data.lotesDisponibles}
                />
              </Section>

              <Section title="Empresas y actividad">
                <KV
                  label="Empresas instaladas"
                  value={data.empresasInstaladas}
                />
                <KV
                  label="Empresas a instalar"
                  value={data.empresasAInstalar}
                />
                <KV
                  label="Principales empresas"
                  value={data.principalesEmpresas}
                />
                <KV
                  label="Actividades permitidas"
                  value={data.actividadesPermitidas}
                />
                <KV
                  label="Productos que exporta"
                  value={data.productosExporta}
                />
              </Section>

              <Section title="Inversión y empleo">
                <KV label="Inversión estimada" value={data.inversion} />
                <KV label="Puestos de trabajo" value={data.puestosTrabajo} />
                <KV label="Exportaciones" value={data.exportaciones} />
              </Section>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default DetalleParqueIndustrial;
